import ProtectedRoute from '@efex-components/protectedRoutes/ProtectedRoutes';
import mixpanelSvc from '@efex-services/mixpanel/mixpanelSvc';
import { Grid } from '@mui/material';
import { selectProfileData } from '@pages/profile/store/selectors';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useEffectOnce from '@main-hooks/useEffectOnce';
import AuthInitializer from '@/modules/auth/AuthInitializer';
import SideBar from './components/SideBar';
import { ONBOARDING_MEXICO_FORM_STEPS } from './constants';
import { FormProvider } from './context';
import OnboardingForm from './steps';
import HeaderActions from '@/components/common/HeaderActions/HeaderActions';

const MexicoOnboarding = () => {
  const userProfile = useSelector(selectProfileData);
  const { isKyc } = userProfile || {};
  const [formStep, setFormStep] = useState(0);
  const [completed, setCompleted] = useState<{
    [k: number]: boolean;
  }>({});

  const countryOrigin = useMemo(
    () => userProfile?.company.country,
    [userProfile?.company.country]
  );

  const entityType = useMemo(
    () => userProfile?.userType || 'PERSON',
    [userProfile?.userType]
  );

  const sendMixpanelEvent = () => {
    mixpanelSvc.track('Onboarding step', {
      step: formStep + 1,
      userType: entityType,
    });
  };

  const isCompany = entityType === 'COMPANY';

  const stepsCompany = useMemo(
    () => [
      t('onbDocuments'),
      t('onbGeneralInformation'),
      t('onbBankInformation'),
      t('onbTransactionalProfile'),
      t('onbStakeHolders'),
    ],
    []
  );

  const stepsPerson = useMemo(
    () => [t('regGeneralInformation'), t('onbBankInformation')],
    []
  );

  const handleComplete = () => {
    const newCompleted = completed;
    newCompleted[formStep] = true;
    setCompleted(newCompleted);
    sendMixpanelEvent();
  };

  useEffectOnce(() => {
    sendMixpanelEvent();
  });

  useEffect(() => {
    if (!isKyc) return;

    setCompleted({
      [ONBOARDING_MEXICO_FORM_STEPS.DOCUMENTATIONS]: true,
      [ONBOARDING_MEXICO_FORM_STEPS.COMPANY_DOCUMENTATION]: true,
      [ONBOARDING_MEXICO_FORM_STEPS.BANK_INFORMATION]: true,
    });

    setFormStep(ONBOARDING_MEXICO_FORM_STEPS.ONBOARDING_CONFIRMATION);
  }, [isKyc]);

  return (
    <ProtectedRoute>
      <AuthInitializer>
        <Grid
          container
          spacing={2}
          sx={{ flexWrap: 'wrap-reverse' }}
          className="min-h-screen"
        >
          <Grid item xs={12} md={3}>
            <SideBar
              currentStep={formStep}
              steps={isCompany ? stepsCompany : stepsPerson}
              isKyc={isKyc}
              setFormStep={setFormStep}
            />
          </Grid>

          <Grid item xs={12} md={9}>
            <HeaderActions />
            <FormProvider>
              <OnboardingForm
                entityType={entityType}
                countryOrigin={countryOrigin}
                formStep={
                  isKyc
                    ? ONBOARDING_MEXICO_FORM_STEPS.ONBOARDING_CONFIRMATION
                    : formStep
                }
                isDisabledEdit={false}
                uploadCompleted={handleComplete}
                setFormStep={setFormStep}
              />
            </FormProvider>
          </Grid>
        </Grid>
      </AuthInitializer>
    </ProtectedRoute>
  );
};

export default MexicoOnboarding;
